import React, { useState } from "react";
import { NavLink, Navigate, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, Inbox, Bot, FolderKanban, Images, Video, Wrench, FileText, Star, HelpCircle, Search, Settings, BarChart3, LogOut, Menu, X, ListOrdered, MapPin, BookOpen, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, area: "all", end: true },
  { to: "/admin/leads", label: "Leads", icon: Inbox, area: "leads" },
  { to: "/admin/ai", label: "AI Training Center", icon: Bot, area: "ai" },
  { to: "/admin/projects", label: "Projects", icon: FolderKanban, area: "content" },
  { to: "/admin/photos", label: "Photo Gallery & Media", icon: Images, area: "content" },
  { to: "/admin/videos", label: "Video Gallery", icon: Video, area: "content" },
  { to: "/admin/services", label: "Services", icon: Wrench, area: "content" },
  { to: "/admin/content", label: "Website Content", icon: FileText, area: "content" },
  { to: "/admin/process", label: "Process Steps", icon: ListOrdered, area: "content" },
  { to: "/admin/reviews", label: "Reviews", icon: Star, area: "content" },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, area: "content" },
  { to: "/admin/areas", label: "Service Areas", icon: MapPin, area: "content" },
  { to: "/admin/seo", label: "SEO", icon: Search, area: "content" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, area: "all" },
  { to: "/admin/settings", label: "Settings", icon: Settings, area: "settings" },
  { to: "/admin/help", label: "Help Guide", icon: BookOpen, area: "all" },
];

export function RequireArea({ area, children }) {
  const { can } = useAuth();
  if (!can(area)) return <div className="bg-white p-10 text-center text-black/60" data-testid="no-access">Your role doesn't have access to this area. Ask the owner if you need it.</div>;
  return children;
}

export default function AdminLayout() {
  const { user, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  if (user === null) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  const side = (
    <nav className="flex h-full flex-col bg-[color:var(--ib-charcoal)] text-white">
      <Link to="/admin" className="flex items-center gap-3 border-b border-white/10 px-5 py-5"><img src="/media/logo-icon.png" alt="" className="h-9" /><span className="font-display text-lg uppercase tracking-wider">Ironbark Admin</span></Link>
      <div className="flex-1 overflow-y-auto py-3">
        {NAV.filter((n) => can(n.area)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.to.split("/").pop() || "dashboard"}`}
            className={({ isActive }) => `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${isActive ? "border-l-4 border-[color:var(--ib-orange)] bg-white/10 pl-4 text-white" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
            <n.icon size={17} />{n.label}
          </NavLink>
        ))}
      </div>
      <div className="border-t border-white/10 p-4 text-sm">
        <p className="truncate font-semibold">{user.name || user.email}</p>
        <p className="text-xs uppercase tracking-wider text-white/50">{user.role.replace("_", " ")}</p>
        <div className="mt-3 flex gap-4">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-white/70 hover:text-white"><ExternalLink size={14} /> View site</a>
          <button onClick={logout} className="flex items-center gap-1 text-white/70 hover:text-white" data-testid="admin-logout-btn"><LogOut size={14} /> Log out</button>
        </div>
      </div>
    </nav>
  );
  return (
    <div className="min-h-screen bg-[color:var(--ib-off)] lg:pl-64" data-testid="admin-layout">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 lg:block">{side}</aside>
      {open && <aside className="fixed inset-0 z-40 w-72 lg:hidden">{side}</aside>}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b bg-white px-4 py-3 lg:hidden">
        <button onClick={() => setOpen(!open)} aria-label="Menu" data-testid="admin-mobile-menu">{open ? <X /> : <Menu />}</button>
        <span className="font-display uppercase">Ironbark Admin</span><span />
      </header>
      <main className="mx-auto max-w-7xl p-4 sm:p-8"><Outlet /></main>
    </div>
  );
}
